import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import dayjs from "dayjs";
import moment from "moment";
import { z } from "zod";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import { WeekDayCircles } from "components/ui/WeekDayCircles";
import { useAddRecurringException } from "features/appointments/hooks/useAddRecurringException";
import { useEditRecurringException } from "features/appointments/hooks/useEditRecurringException";
import { reportFormErrors } from "utils/reportFormErrors";

import style from "./AddRecurringExceptionForm.module.css";
import { dayNumbersToText, dayTextToNumber } from "./utils";

const WEEKDAY_IDS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const weekdayFromDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = dayjs(yyyyMmDd, "YYYY-MM-DD");
  if (!d.isValid()) return null;
  // dayjs/js Date getDay() is Sunday=0..Saturday=6 — map to ISO index.
  const jsDay = d.day();
  const index = jsDay === 0 ? 6 : jsDay - 1;
  return WEEKDAY_IDS[index];
};

// Legacy rows from older versions of this form may carry multiple days in
// the CSV ("1,3,5") or a non-CUSTOM recurrenceType (DAY/WEEK/MONTH). The
// form is permissive when prefilling — take the first day from the CSV,
// or fall back to the start date's weekday if the CSV is empty — and
// saving an edited legacy row normalises it to the new shape (CUSTOM,
// interval 1, one day).
const pickInitialWeekday = (initialData) => {
  const csv = initialData?.recurrenceDaysOfWeek;
  if (typeof csv === "string" && csv.length > 0) {
    const [first] = dayNumbersToText(csv.split(","));
    if (first) return first;
  }
  if (initialData?.startDate) {
    return weekdayFromDate(initialData.startDate);
  }
  return null;
};

const formSchema = z
  .object({
    reason: z.string(),
    type: z.string().min(1, { message: "Please fill in the type." }),
    doctor: z.string().min(1, { message: "Please select doctor." }),
    fromDate: z.string().min(1, {
      message: "Please select the from date field.",
    }),
    toDate: z.string().min(1, {
      message: "Please select the to date field.",
    }),
    fromTime: z.string(),
    toTime: z.string(),
    allDay: z.boolean(),
    weekday: z
      .string()
      .min(1, { message: "Please choose the day of the week." }),
  })
  .superRefine((data, ctx) => {
    if (!data.allDay) {
      if (!data.fromTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select the from time field.",
          path: ["fromTime"],
        });
      }
      if (!data.toTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select the to time field.",
          path: ["toTime"],
        });
      }
      if (data.fromTime && data.toTime && data.fromTime >= data.toTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "From time must be before to time.",
          path: ["toTime"],
        });
      }
    }
    if (data.fromDate && data.toDate && data.toDate <= data.fromDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date.",
        path: ["toDate"],
      });
    }
  });

export const AddRecurringExceptionForm = ({
  initialData,
  closeModal,
  allTimes,
  staffMembers,
  initialStaffMember,
  refetchCalendar,
  initialStartDate,
  initialStartTime,
  exceptionTypes,
}) => {
  const [fromDateValue, setFromDateValue] = useState(() => {
    if (initialData?.startDate) {
      return dayjs(initialData.startDate, "YYYY-MM-DD").toDate();
    }
    if (initialStartDate) {
      return dayjs(initialStartDate, "DD-MM-YYYY").toDate();
    }
    return new Date();
  });
  const [toDateValue, setToDateValue] = useState(() => {
    if (initialData?.endDate) {
      return dayjs(initialData.endDate, "YYYY-MM-DD").toDate();
    }
    if (initialStartDate) {
      return dayjs(initialStartDate, "DD-MM-YYYY").add(1, "day").toDate();
    }
    return dayjs().add(1, "day").toDate();
  });

  const { mutate: addRecurringException, isPending: isAddPending } =
    useAddRecurringException({
      onSuccess: () => {
        closeModal();
        refetchCalendar?.();
      },
    });

  const { mutate: editRecurringException, isPending: isEditPending } =
    useEditRecurringException({
      onSuccess: () => {
        closeModal();
        refetchCalendar?.();
      },
    });

  const initialValuesStartTime =
    initialData?.startAt &&
    initialData?.startAt
      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
      ?.join(":");

  const initialValuesEndTime =
    initialData?.endAt &&
    initialData?.endAt
      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
      ?.join(":");

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      weekday:
        pickInitialWeekday(initialData) ||
        weekdayFromDate(
          initialStartDate
            ? dayjs(initialStartDate, "DD-MM-YYYY").format("YYYY-MM-DD")
            : format(new Date(), "yyyy-MM-dd")
        ) ||
        "monday",
      // Editing shows the exception's staff member. New from a column
      // uses the column's doctor (initialStaffMember). New from no
      // column context falls back to the first doctor in the dropdown,
      // not the logged-in user.
      doctor:
        initialData?.staffMemberId != null
          ? String(initialData.staffMemberId)
          : initialStaffMember != null
          ? String(initialStaffMember)
          : staffMembers?.[0]?.id != null
          ? String(staffMembers[0].id)
          : "",
      type: initialData?.exceptionType || exceptionTypes[0] || "",
      reason: initialData?.exceptionReason || "",
      fromDate: initialData?.startDate
        ? dayjs(initialData.startDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : initialStartDate
        ? dayjs(initialStartDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : format(new Date(), "yyyy-MM-dd"),
      toDate: initialData?.endDate
        ? dayjs(initialData.endDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : initialStartDate
        ? dayjs(initialStartDate, "DD-MM-YYYY").add(1, "day").format("YYYY-MM-DD")
        : dayjs().add(1, "day").format("YYYY-MM-DD"),
      fromTime:
        initialValuesStartTime ||
        initialStartTime ||
        allTimes[0].formattedRange.split(" - ")[0],
      toTime:
        initialValuesEndTime ||
        allTimes
          .find(
            (item) =>
              item?.formattedRange?.split(" - ")?.[0] === initialStartTime
          )
          ?.formattedRange?.split(" - ")[1] ||
        allTimes[0].formattedRange.split(" - ")[1],
      allDay: initialData?.allDay || false,
    },
  });

  const handleInvalidSubmit = (errors) =>
    reportFormErrors({ formName: "AddRecurringExceptionForm", errors });

  const handleFormSubmit = async (values) => {
    const isoDay = dayTextToNumber(values.weekday);
    // Single day that repeats weekly. The backend's CUSTOM branch treats
    // interval as a no-op and expects a single-element daysOfWeek list,
    // so this maps cleanly onto the existing endpoint without an API
    // change. Legacy multi-day or non-CUSTOM rows are normalised to this
    // shape on save (the chosen weekday wins).
    const payload = {
      staffMemberId: Number(values.doctor),
      type: values.type,
      startAt: values.fromTime,
      endAt: values.toTime,
      startTime: values.fromDate,
      endTime: values.toDate,
      reason: values.reason,
      allDay: values.allDay,
      recurrenceType: "CUSTOM",
      daysOfWeek: [isoDay],
      interval: 1,
    };

    if (initialData) {
      editRecurringException({
        ...payload,
        exceptionId: initialData?.exceptionId,
      });
    } else {
      addRecurringException(payload);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        className={style.addLetterForm}
        onSubmit={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
      >
        <div className={style.inputGroupGrid} style={{ maxWidth: "100%" }}>
          <FormField name="doctor">
            <label className={style.addLetterFormLabel}>Doctor</label>
            <Select
              name="doctor"
              options={staffMembers.map((staffMember) => ({
                label: `${staffMember.active ? "● " : ""}${
                  staffMember.firstName
                } ${staffMember.lastName}${
                  staffMember.staffRole
                    ? ` (${staffMember.staffRole.toLowerCase()})`
                    : ""
                }`,
                value: String(staffMember.id),
              }))}
              value={form.watch("doctor")}
              onChange={(value) => form.setValue("doctor", value)}
              placeholder={null}
              fullWidth
            />
          </FormField>
          <FormField name="type">
            <label className={style.addLetterFormLabel}>Type</label>
            <Select
              name="type"
              options={(exceptionTypes || []).map((exceptionType) => ({
                label: exceptionType,
                value: exceptionType,
              }))}
              value={form.watch("type")}
              onChange={(value) => form.setValue("type", value)}
              placeholder={null}
              fullWidth
            />
          </FormField>
        </div>
        <div className={style.inputGroupGrid4}>
          <FormField name="fromDate">
            <label className={style.addLetterFormLabel}>From date</label>
            <CustomCalendar
              value={fromDateValue}
              onChange={(date) => {
                setFromDateValue(date);
                if (date) {
                  form.setValue("fromDate", moment(date).format("YYYY-MM-DD"));
                }
              }}
              placeholder="Select from date"
            />
          </FormField>

          <FormField name="fromTime">
            <label className={style.addLetterFormLabel}>From time</label>
            <Select
              name="fromTime"
              options={allTimes.map((time) => ({
                label: time.formattedRange.split(" - ")[0],
                value: time.formattedRange.split(" - ")[0],
              }))}
              value={form.watch("fromTime")}
              onChange={(value) => form.setValue("fromTime", value)}
              disabled={!!form.watch("allDay")}
              placeholder={null}
              fullWidth
            />
          </FormField>
          <FormField name="toDate">
            <label className={style.addLetterFormLabel}>Until date</label>
            <CustomCalendar
              value={toDateValue}
              onChange={(date) => {
                setToDateValue(date);
                if (date) {
                  form.setValue("toDate", moment(date).format("YYYY-MM-DD"));
                }
              }}
              placeholder="Select until date"
            />
          </FormField>

          <FormField name="toTime">
            <label className={style.addLetterFormLabel}>To time</label>
            <Select
              name="toTime"
              options={allTimes.map((time) => ({
                label: time.formattedRange.split(" - ")[1],
                value: time.formattedRange.split(" - ")[1],
              }))}
              value={form.watch("toTime")}
              onChange={(value) => form.setValue("toTime", value)}
              disabled={!!form.watch("allDay")}
              placeholder={null}
              fullWidth
            />
          </FormField>
        </div>
        <div className={style.checkboxWrapper}>
          <label className={`${style.pregnancyFormCheckboxLabel}`}>
            <CustomCheckbox
              checked={form.watch("allDay")}
              onChange={(e) => {
                form.clearErrors();
                form.setValue("allDay", e.target.checked);
              }}
            />
            <p>all day</p>
          </label>
        </div>
        <FormField name="weekday">
          <label className={style.addLetterFormLabel}>Repeats on</label>
          <WeekDayCircles
            value={form.watch("weekday")}
            onChange={(day) =>
              form.setValue("weekday", day, { shouldValidate: true })
            }
          />
        </FormField>
        <FormField name="reason">
          <label className={style.addLetterFormLabel}>Reason</label>
          <textarea
            className="form-control textareaform-control"
            style={{ minHeight: "100px" }}
            {...form.register("reason")}
          />
        </FormField>
        <div className={style.buttonGroup}>
          <Button
            type="button"
            variant="outline"
            size="large"
            disabled={isAddPending || isEditPending}
            onClick={() => {
              closeModal();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={isAddPending || isEditPending}
          >
            Confirm
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
